import { Either, Option, TaskEither } from "fp";

import React from "react";
import * as ReactQuery from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";

import * as Context from "~/Context";
import { Request } from "~/Request";

/**
 * `Remote<A>` represents any `A` which can be requested and received from some
 * remote resource.
 */
export type Remote<A> =
  | { readonly remote: "idle" }
  | { readonly remote: "loading" }
  | { readonly remote: Either<Error, A> };

export namespace Remote {
  export function idle<A>(): Remote<A> {
    return { remote: "idle" };
  }

  export function loading<A>(): Remote<A> {
    return { remote: "loading" };
  }

  export function received<A>(received: Either<Error, A>): Remote<A> {
    return { remote: received };
  }

  export function success<A>(success: A): Remote<A> {
    return received(Either.right(success));
  }

  export function error<A>(error: Error): Remote<A> {
    return received(Either.left(error));
  }

  export function isLoading<A>({ remote }: Remote<A>): boolean {
    return remote === "loading";
  }

  export function isError<A>({ remote }: Remote<A>): boolean {
    return remote !== "idle" && remote !== "loading" && Either.isLeft(remote);
  }

  export function isSuccess<A>({ remote }: Remote<A>): boolean {
    return remote !== "idle" && remote !== "loading" && Either.isRight(remote);
  }

  export function equals<A>(equals: (a: A) => (b: A) => boolean) {
    return (a: Remote<A>) => (b: Remote<A>): boolean => {
      if (a.remote === b.remote) return true;
      if (typeof a.remote === "string" || typeof b.remote === "string")
        return false;

      if (Either.isLeft(a.remote) && Either.isLeft(b.remote)) return true;
      return (
        Either.isRight(a.remote) &&
        Either.isRight(b.remote) &&
        equals(a.remote.right)(b.remote.right)
      );
    };
  }

  export function map<A, B>(fn: (a: A) => B) {
    return ({ remote }: Remote<A>): Remote<B> => ({
      remote: typeof remote === "string" ? remote : Either.map(fn)(remote),
    });
  }

  export function toEither<A>(remote: Remote<A>): Option<Either<Error, A>> {
    return when<A, Option<Either<Error, A>>>(remote, {
      idle: () => Option.none,
      loading: () => Option.none,
      error: (error) => Option.some(Either.left(error)),
      success: (success) => Option.some(Either.right(success)),
    });
  }

  export function toEitherErrorOr<A>() {
    return (remote: Remote<A>) => toEither<A>(remote);
  }

  export function getOrElse<A>(orElse: () => A) {
    return (remote: Remote<A>): A =>
      when(remote, {
        loading: orElse,
        error: orElse,
        success: (success) => success,
      });
  }

  export function getOrElseW<A, B>(orElse: () => B) {
    return getOrElse<A | B>(orElse);
  }

  export function toOption<A>(remote: Remote<A>): Option<A> {
    return when(remote, {
      loading: () => Option.none,
      error: () => Option.none,
      success: Option.some,
    });
  }

  export function toUndefined<A>(remote: Remote<A>): A | undefined {
    return when(remote, {
      loading: () => undefined,
      error: () => undefined,
      success: (success) => success,
    });
  }

  export type When<A, B> = {
    readonly idle?: () => B;
    readonly loading: () => B;
    readonly error: (error: Error) => B;
    readonly success: (a: A) => B;
  };

  /**
   * Convert `Data.Remote<A>` to a `B`, which requires handling each case of the
   * data structure, i.e. `idle`, `loading`, or `received`...
   *
   * ```ts
   * pipe(
   *   users,
   *   Data.Remote.when({
   *     idle: () => <>Select a user to search for.</>,
   *     loading: () => <>Loading...</>,
   *     error: error => <>Something went wrong! {error.message}</>,
   *     success: users => <div>Found {users.length} results...</div>
   *   })
   * ) ===
   *   Data.Remote.when(users, {
   *     idle: () => <>Select a user to search for.</>,
   *     loading: () => <>Loading...</>,
   *     error: error => <>Something went wrong! {error.message}</>,
   *     success: users => <div>Found {users.length} results...</div>
   *   })
   * ```
   */
  export function when<A, B>(when: When<A, B>): (remote: Remote<A>) => B;
  export function when<A, B>(remote: Remote<A>, when: When<A, B>): B;
  export function when<A, B>(
    whenOrRemote: Remote<A> | When<A, B>,
    whenOrUndefined?: When<A, B>
  ): B | ((remote: Remote<A>) => B) {
    if (whenOrUndefined)
      return when(whenOrUndefined)(whenOrRemote as Remote<A>);

    const { loading, idle = loading, error, success } = whenOrRemote as When<
      A,
      B
    >;

    return ({ remote }: Remote<A>): B => {
      switch (remote) {
        case "idle":
          return idle();
        case "loading":
          return loading();
        default:
          return Either.fold(error, success)(remote);
      }
    };
  }

  export const Provider = ({
    children,
  }: {
    readonly children?: React.ReactNode;
  }) => {
    const client = React.useMemo(
      () =>
        new ReactQuery.QueryClient({
          defaultOptions: { queries: { refetchOnWindowFocus: false } },
        }),
      []
    );
    return (
      <ReactQuery.QueryClientProvider client={client}>
        <ReactQueryDevtools position="bottom-left" />
        {children}
      </ReactQuery.QueryClientProvider>
    );
  };

  export namespace Query {
    export function use<A>(
      key: ReactQuery.QueryKey,
      request: (context: Context.Context) => TaskEither.ErrorOr<A>,
      options?: ReactQuery.UseQueryOptions<Either.ErrorOr<A>, Error>
    ): Remote<A> {
      const { error, data } = ReactQuery.useQuery<Either.ErrorOr<A>, Error>(
        key,
        request(Context.useContext()),
        options
      );

      return data
        ? Remote.received(data)
        : error
        ? Remote.error(error)
        : Remote.loading();
    }

    /**
     * {@link use}, over a list whose length is not fixed. Calling `use` in a
     * loop would tie the number of hooks to the length of that list and break
     * the moment it changed, which is what `useQueries` exists to avoid.
     *
     * Results share the cache with {@link use}, so a query issued here and the
     * same query issued later by `use` are one request, not two.
     *
     * The casts restore the element type: this version of react-query declares
     * `useQueries` without type parameters — `(queries: UseQueryOptions[]) =>
     * UseQueryResult[]` — so both sides come back as `unknown`. The shape being
     * asserted is the same one `use` above already relies on.
     */
    export function useAll<A>(
      queries: readonly {
        readonly key: ReactQuery.QueryKey;
        readonly request: (context: Context.Context) => TaskEither.ErrorOr<A>;
      }[],
      options?: ReactQuery.UseQueryOptions<Either.ErrorOr<A>, Error>
    ): readonly Remote<A>[] {
      const context = Context.useContext();

      const results = (ReactQuery.useQueries(
        queries.map(({ key, request }) => ({
          ...options,
          queryKey: key,
          queryFn: request(context),
        })) as // `useQueries` takes a mutable array, so this one cast cannot be
        // `readonly` — hence the exemption rather than the repo default.
        // eslint-disable-next-line functional/prefer-readonly-type
        ReactQuery.UseQueryOptions[]
      ) as unknown) as readonly ReactQuery.UseQueryResult<
        Either.ErrorOr<A>,
        Error
      >[];

      return results.map(({ error, data }) =>
        data
          ? Remote.received(data)
          : error
          ? Remote.error(error)
          : Remote.loading()
      );
    }
  }

  export namespace Fetch {
    export function use<A>(
      key: ReactQuery.QueryKey,
      request: (context: Context.Context) => TaskEither.ErrorOr<A>,
      options?: ReactQuery.UseQueryOptions<Either.ErrorOr<A>, Error>
    ) {
      const { refetch } = ReactQuery.useQuery<Either.ErrorOr<A>, Error>(
        key,
        request(Context.useContext()),
        options
      );

      return refetch;
    }
  }

  export namespace Mutation {
    export type MutationResponse<A, Args extends readonly unknown[]> = {
      readonly request: (...args: Args) => void;
      readonly response: Remote<A>;
      readonly reset: () => void;
    };

    export function use<A, Args extends readonly unknown[]>(
      request: (...args: Args) => Request.Fn<A>,
      options?: ReactQuery.UseMutationOptions<
        Either.ErrorOr<A>,
        Error,
        Args,
        unknown
      >
    ): MutationResponse<A, Args> {
      const context = Context.useContext();
      const { mutate, isLoading, error, data, reset } = ReactQuery.useMutation<
        Either.ErrorOr<A>,
        Error,
        Args
      >((args) => request(...args)(context)(), { ...options });

      return {
        request: (...args: Args) => mutate(args),
        response: isLoading
          ? Remote.loading()
          : error
          ? Remote.error(error)
          : data
          ? Remote.received(data)
          : Remote.idle(),
        reset,
      };
    }
  }
}
